import React, { useEffect, useState } from 'react';
import { SWRConfig } from 'swr';
import { Routes, Route, Outlet, Navigate, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { PageHeader } from '@components/organisms';
import { PageWrapper, BodyWrapper, Dialog } from '@components/templates';
import {
  Login,
  Info,
  History,
  HistoryDetail,
  Registration,
  UserPermission,
  AdminLog,
  UserLog,
  PacsCloudSetting,
  PacsPremiseSetting,
  MailSetting,
  Metatag,
  Notification,
  Setting,
  Report,
  Support,
  LicenseDetail,
  CreateAccount,
  UserPermissionDetail,
  RegisterLicense,
  Transfer,
  TransferLog,
  CompanyLog,
  PACSLog,
  NIPDOption,
  ADOption,
  SCSOption,
  NiIpdSendToPacs,
  ADSendToPacs,
  SCSSendToPacs,
  CTPSendToPacs,
  CustomMetaTag,
  NMSendToPacs,
  NMOption,
  CTPOption,
  CTAOption,
  CTASendToPacs,
} from '@pages/pages';
import fetch from 'unfetch';

// runtime 환경 변수 적용
import setupRuntimeEnv from './setupRuntimeEnv';
import ErrorBE from '@components/templates/Error/ErrorBE';
import Error404 from '@components/templates/Error/Error404';
import Sending from '@pages/Transfer/Transfer/Sending';
import Receiving from '@pages/Transfer/Transfer/Receiving';
import { useRecoilState } from 'recoil';
import { hasSessionState } from '@pages/Permissions/User';

setupRuntimeEnv();

/**
 * 제품명
 * 각 제품별 노출 분기를 위해 생성 및 사용
 */
export const PRODUCT_TITLE = window.__RUNTIME_CONFIG__.REACT_APP_TITLE || 'heuron';
export const PRODUCT_CODE = window.__RUNTIME_CONFIG__.REACT_APP_PRODUCT_CODE;
export const PRODUCT_TYPE = window.__RUNTIME_CONFIG__.REACT_APP_TYPE || 'onPremise';

export const ADMIN_ACCESS_TOKEN_KEY = window.__RUNTIME_CONFIG__.REACT_APP_ACCESS_TOKEN_KEY || 'PROD_ADMIN_ACCESS_TOKEN';
export const ADMIN_EXPIRES_IN = `${PRODUCT_TITLE}_ADMIN_EXPIRES_IN`;

const PrivateOutlet = () => {
  const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
  if (!newToken) {
    return <Navigate to={'/'} replace={true} />;
  }
  return <Outlet />;
};

export type FetcherArgs<T = any> = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'UPDATE' | 'PATCH';
  url?: string;
  accessToken?: string;
  body?: T;
  headers?: { [key: string]: any };
};
export const BASE_URL = window.__RUNTIME_CONFIG__.REACT_APP_DOMAIN || '';
export const fetcher = <T = any,>({ method = 'GET', url, accessToken = '', body }: FetcherArgs) =>
  fetch(`${BASE_URL}/${url}`, {
    method: method,
    headers: {
      accept: '*/*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body || {}),
  }).then<T>((r) => {
    return r.json();
  });

export const getFetcher = ({ method = 'GET', accessToken = '', body }: FetcherArgs) => {
  return (url: string) =>
    fetch(`${BASE_URL}/${url}`, {
      method: `${method}`,
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'omit',
      body: JSON.stringify(body || {}),
    })
      .then((res) => res.json())
      .catch((e) => e);
};

export const getFetcherFormData = ({ method = 'GET', accessToken = '', body, headers }: FetcherArgs) => {
  return (url: string) =>
    fetch(`${BASE_URL}/${url}`, {
      method: `${method}`,
      headers: {
        accept: '*/*',
        Authorization: `Bearer ${accessToken}`,
        ...headers,
      },
      credentials: 'omit',
      body,
    })
      .then((res) => res.json())
      .catch((e) => e);
};

export const _fetch = window.fetch;

export const getWindowFetcher = ({ method = 'GET', accessToken = '', body, headers }: FetcherArgs) => {
  return (url: string) =>
    _fetch(`${BASE_URL}/${url}`, {
      method: `${method}`,
      headers: {
        accept: '*/*',
        Authorization: `Bearer ${accessToken}`,
        ...headers,
      },
      body,
    })
      .then((res) => res)
      .catch((e) => e);
};

function App() {
  const navigate = useNavigate();
  const [hasSession, setHasSession] = useRecoilState(hasSessionState);

  useEffect(() => {
    (async () => {
      const url = `api/menu`;
      const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
      const localExpy = localStorage.getItem(ADMIN_EXPIRES_IN);
      const tokenExpired = localExpy ? new Date().getTime() > Number(localExpy) : true;

      if (tokenExpired) {
        localStorage.removeItem('PROD_ADMIN_ACCESS_TOKEN');
        navigate && navigate('/', { replace: true });
        setHasSession(false);
      } else {
        await getFetcher({
          method: 'GET',
          accessToken: token,
        })(url)
          .then(async (r) => {
            if (r.code !== '200') {
              localStorage.removeItem('PROD_ADMIN_ACCESS_TOKEN');
              navigate && navigate('/', { replace: true });
              return;
            }
            setHasSession(true);
          })
          .catch((e) => {
            // noneMenuData();
            console.log('menu data load Err - session');
          });
      }
    })();
  }, []);

  return (
    <SWRConfig
      value={{
        fetcher,
      }}
    >
      <StyledAppWrapper className="App">
        <Routes>
          <Route
            path={''}
            element={
              <>
                <PageHeader />
                <Login />
              </>
            }
          />
          <Route
            path={'register'}
            element={
              <>
                <PageHeader />
                <RegisterLicense />
              </>
            }
          />
          {hasSession && (
            <>
              <Route path={'popup'} element={<PrivateOutlet />}>
                <Route path={'License-detail/:licenseId'} element={<LicenseDetail />} />
                <Route path={'create-account'} element={<CreateAccount />} />
              </Route>

              {/* license */}
              <Route path={'/'} element={<PrivateOutlet />}>
                <Route
                  path={'license'}
                  element={
                    <PageWrapper>
                      <BodyWrapper>
                        {/*<label>License</label>*/}
                        <Outlet />
                      </BodyWrapper>
                    </PageWrapper>
                  }
                >
                  <Route path={''} element={<Info />} />
                  <Route path={'history'} element={<Outlet />}>
                    <Route index element={<History />} />
                    <Route path={'detail/:id'} element={<HistoryDetail />} />
                  </Route>
                  <Route path={'registration'} element={<Registration />} />
                </Route>
                {/* permissions */}
                <Route
                  path={'permissions'}
                  element={
                    <PageWrapper>
                      <BodyWrapper>
                        <Outlet />
                      </BodyWrapper>
                    </PageWrapper>
                  }
                >
                  <Route
                    path={''}
                    element={
                      <>
                        <label>User (Product User)</label>
                        <Outlet />
                      </>
                    }
                  >
                    <Route path={''} element={<UserPermission />} />
                    <Route
                      path={'detail/:userNumber'}
                      element={
                        <>
                          <label>Detail</label> <UserPermissionDetail />
                        </>
                      }
                    />
                  </Route>
                </Route>
                {/* log */}
                <Route
                  path={'log'}
                  element={
                    <PageWrapper>
                      <BodyWrapper>
                        <Outlet />
                      </BodyWrapper>
                    </PageWrapper>
                  }
                >
                  <Route path={''} element={<AdminLog />} />
                  <Route path={'user'} element={<UserLog />} />
                  <Route path={'pacs'} element={<PACSLog />} />
                </Route>
                {/* sw */}
                <Route
                  path={'sw'}
                  element={
                    <PageWrapper>
                      <BodyWrapper>
                        <Outlet />
                      </BodyWrapper>
                    </PageWrapper>
                  }
                >
                  <Route path={''} element={<Setting />} />
                  <Route path={'pacs_cloud'} element={<PacsCloudSetting />} />
                  <Route path={'pacs_premise'} element={<PacsPremiseSetting />} />
                  {/**  제품 setting 별 send to pacs 메뉴가 생기면 해당 report 불필요 해짐 - 각 제품 단으로 들어감  **/}
                  <Route path={'report'} element={<Report />} />
                  <Route path={'mail'} element={<MailSetting />} />
                  <Route path={'metatag'} element={<Metatag />} />
                </Route>

                {/* app 별 Setting */}
                <Route
                  path={'setting'}
                  element={
                    <PageWrapper>
                      <BodyWrapper>
                        <Outlet />
                      </BodyWrapper>
                    </PageWrapper>
                  }
                >
                  {(PRODUCT_CODE === 'mpdia' ||
                    PRODUCT_CODE === 'mn1q' ||
                    PRODUCT_CODE === 'atrophy' ||
                    PRODUCT_CODE === 'stroke' ||
                    PRODUCT_CODE === 'nm' ||
                    PRODUCT_CODE === 'ctp' ||
                    PRODUCT_CODE === 'cta') && (
                    <>
                      <Route path={'metatag'} element={<CustomMetaTag />} />
                    </>
                  )}

                  {(PRODUCT_CODE === 'mpdia' || PRODUCT_CODE === 'mn1q') && (
                    <>
                      <Route path={''} element={<NiIpdSendToPacs />} />
                      <Route path={'option'} element={<NIPDOption />} />
                    </>
                  )}

                  {PRODUCT_CODE === 'nm' && (
                    <>
                      <Route path={''} element={<NMSendToPacs />} />
                      <Route path={'option'} element={<NMOption />} />
                    </>
                  )}

                  {PRODUCT_CODE === 'atrophy' && (
                    <>
                      <Route path={''} element={<ADSendToPacs />} />
                      <Route path={'option'} element={<ADOption />} />
                    </>
                  )}

                  {PRODUCT_CODE === 'stroke' && (
                    <>
                      <Route path={''} element={<SCSSendToPacs />} />
                      <Route path={'option'} element={<SCSOption />} />
                      <Route path={'notification'} element={<Notification />} />
                    </>
                  )}

                  {PRODUCT_CODE === 'ctp' && (
                    <>
                      <Route path={''} element={<CTPSendToPacs />} />
                      {/* <Route path={'default'} element={<CTPDefaultValue />} /> */}
                      <Route path={'option'} element={<CTPOption />} />
                    </>
                  )}
                  {PRODUCT_CODE === 'cta' && (
                    // CTA
                    <>
                      <Route path={''} element={<CTASendToPacs />} />
                      <Route path={'option'} element={<CTAOption />} />
                    </>
                  )}
                </Route>

                {PRODUCT_CODE === 'stroke' && (
                  <>
                    <Route
                      path={'transfer'}
                      element={
                        <PageWrapper>
                          <BodyWrapper>
                            <Outlet />
                          </BodyWrapper>
                        </PageWrapper>
                      }
                    >
                      <Route path={''} element={<Transfer />} />
                      <Route path={'sending/:companyId'} element={<Sending />} />
                      <Route path={'receiving/:companyId'} element={<Receiving />} />
                      <Route path={'log'} element={<TransferLog />} />
                      <Route path={'companylog'} element={<CompanyLog />} />
                    </Route>
                  </>
                )}
                {/* CTA Setting */}
                {/* support */}
                <Route
                  path={'support'}
                  element={
                    <PageWrapper>
                      <Support />
                    </PageWrapper>
                  }
                />
                <Route path={'error'} element={<ErrorBE />} />
                <Route path={'*'} element={<Error404 />} />
              </Route>
            </>
          )}
        </Routes>

        <>
          <Dialog type={'form'} />
          <Dialog />
        </>
      </StyledAppWrapper>
    </SWRConfig>
  );
}

//#region styled components

const StyledAppWrapper = styled.div`
  display: grid;
  grid-template-rows: 80px 1fr;
  grid-template-columns: 241px 1fr;
  grid-template-areas:
    'hd hd'
    'nav main';
  header {
    grid-area: hd;
    box-sizing: border-box;
  }
  nav {
    grid-area: nav;
  }
  main {
    grid-area: main;
  }

  @media (max-width: 412px) {
    grid-template-rows: 80px 1fr;
    grid-template-columns: 100%;
    grid-template-areas:
      'hd'
      'main';
    header {
      width: 100%;
      :before {
        content: '';
        width: 20px;
        height: 20px;
        position: absolute;
      }
    }
    nav {
      position: absolute;
      left: -241px;
      top: 80px;
      z-index: 999;
      background-color: white;
    }
    main {
      padding: 20px;
      min-width: unset;
      max-width: 100vw;
      overflow: scroll;
      min-height: calc(100vh - 80px);
    }
  }
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

//#region S/W styled components

export const SwButtonSection = styled.section`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  & button {
    margin-top: 0;
  }
`;

//#endregion

//#endregion

export default App;
